import {
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  EventType,
  PublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser';
import { runtimeConfig } from '@/runtimeConfig';

const tenantId = runtimeConfig.MsalTenantId.Or(
  'b5a920d6-7d3c-44fe-baad-4ffed6b8774d',
);
const authority = tenantId
  ? `https://login.microsoftonline.com/${tenantId}`
  : undefined;
const clientId = runtimeConfig.MsalClientId.Or('');
const redirectUri = `${window.location.origin}/redirect.html`;

function isUsableClientId(value: string) {
  const guidPattern = /^[0-9a-fA-F-]{36}$/;
  return (
    guidPattern.test(value) && value !== '00000000-0000-0000-0000-000000000000'
  );
}

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    redirectUri,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
};

function isMsalEnabled() {
  return runtimeConfig.MsalEnabled.Or('true').trim().toLowerCase() === 'true';
}

export const msalInstance = new PublicClientApplication(msalConfig);
let msalInitPromise: Promise<void> | null = null;
let loginEventRegistered = false;

const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'User.Read'],
  redirectUri,
};

function applyActiveAccount(authResult?: AuthenticationResult | null) {
  if (authResult?.account) {
    msalInstance.setActiveAccount(authResult.account);
    return;
  }

  if (!msalInstance.getActiveAccount()) {
    const fallbackAccount = msalInstance.getAllAccounts()[0];
    if (fallbackAccount) {
      msalInstance.setActiveAccount(fallbackAccount);
    }
  }
}

function isInteractionInProgressError(error: unknown) {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as { errorCode?: string; message?: string };
  return (
    candidate.errorCode === 'interaction_in_progress' ||
    candidate.message?.includes('interaction_in_progress') === true
  );
}

function clearStaleMsalInteractionState() {
  const isInteractionKey = (key: string) =>
    key.toLowerCase().includes('msal') &&
    key.toLowerCase().includes('interaction') &&
    key.toLowerCase().includes('status');

  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index);
    if (key && isInteractionKey(key)) {
      sessionStorage.removeItem(key);
    }
  }

  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key && isInteractionKey(key)) {
      localStorage.removeItem(key);
    }
  }
}

export async function initializeMsal() {
  if (!isMsalEnabled()) {
    return;
  }

  if (msalInitPromise) {
    return msalInitPromise;
  }

  msalInitPromise = (async () => {
    if (!loginEventRegistered) {
      msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const result = event.payload as AuthenticationResult;
          msalInstance.setActiveAccount(result.account);
        }
      });
      loginEventRegistered = true;
    }

    await msalInstance.initialize();
    const result = await msalInstance.handleRedirectPromise();
    applyActiveAccount(result);

    if (!isUsableClientId(clientId)) {
      throw new Error(
        'MSAL login failed: MsalClientId (APP_CONFIG) or VITE_MSAL_CLIENT_ID is missing or invalid.',
      );
    }

    let account =
      msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) {
      try {
        await msalInstance.loginRedirect({
          ...loginRequest,
          prompt: 'select_account',
        });
      } catch (error) {
        if (isInteractionInProgressError(error)) {
          clearStaleMsalInteractionState();

          try {
            await msalInstance.loginRedirect({
              ...loginRequest,
              prompt: 'select_account',
            });
          } catch (retryError) {
            if (isInteractionInProgressError(retryError)) {
              throw new Error(
                'MSAL login failed: another authentication interaction is already in progress.',
              );
            }
            throw retryError;
          }
          return;
        }
        throw error;
      }
    }

    account =
      msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) {
      throw new Error(
        'MSAL login failed: no authenticated Entra account is available.',
      );
    }
  })();

  return msalInitPromise;
}

export function loginWithMicrosoft() {
  if (!isMsalEnabled()) {
    return Promise.resolve();
  }

  return msalInstance.loginRedirect(loginRequest);
}

export function isSignedInWithMsal() {
  return Boolean(
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0],
  );
}

export function getMsalDisplayName() {
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  return account?.name?.trim() || account?.username?.trim() || '';
}

function readStringClaim(claims: Record<string, unknown>, key: string) {
  const value = claims[key];
  return typeof value === 'string' ? value.trim() : '';
}

function readStringArrayClaim(claims: Record<string, unknown>, key: string) {
  const value = claims[key];
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
}

function readFirstStringFromUnknown(value: unknown) {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    const firstString = value.find(
      (item): item is string =>
        typeof item === 'string' && item.trim().length > 0,
    );
    return firstString?.trim() ?? '';
  }

  return '';
}

function getClaimsFromAccount(account?: AccountInfo | null) {
  if (!account?.idTokenClaims || typeof account.idTokenClaims !== 'object') {
    return null;
  }

  return account.idTokenClaims as Record<string, unknown>;
}

export function getJobTitleFromAccount(account?: AccountInfo | null) {
  const claims = getClaimsFromAccount(account);
  if (!claims) {
    return '';
  }

  const jobTitleClaimCandidates = [
    'jobTitle',
    'job_title',
    'title',
    'occupation',
  ];

  for (const claimName of jobTitleClaimCandidates) {
    const claimValue = readStringClaim(claims, claimName);
    if (claimValue) {
      return claimValue;
    }
  }

  return '';
}

export function getEmailFromAccount(account?: AccountInfo | null) {
  const claims = getClaimsFromAccount(account);
  if (claims) {
    const emailClaimCandidates = [
      'preferred_username',
      'upn',
      'email',
      'emails',
    ];
    for (const claimName of emailClaimCandidates) {
      const claimValue = readFirstStringFromUnknown(claims[claimName]);
      if (claimValue) {
        return claimValue;
      }
    }
  }

  return account?.username?.trim() || '';
}

export function getRoleLabelFromAccount(account?: AccountInfo | null) {
  const claims = getClaimsFromAccount(account);

  if (claims) {
    const roles = readStringArrayClaim(claims, 'roles');
    if (roles.length > 0) {
      return roles[0];
    }

    const candidateClaims = [
      'department',
      'extension_Role',
      'extension_role',
      'role',
    ];

    for (const claimName of candidateClaims) {
      const claimValue = readStringClaim(claims, claimName);
      if (claimValue) {
        return claimValue;
      }
    }

    const jobTitle = getJobTitleFromAccount(account);
    if (jobTitle) {
      return jobTitle;
    }
  }

  return getEmailFromAccount(account);
}

export function getMsalRoleLabel() {
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  return getRoleLabelFromAccount(account);
}

export function getMsalRoles(account?: AccountInfo | null): string[] {
  const targetAccount =
    account ??
    msalInstance.getActiveAccount() ??
    msalInstance.getAllAccounts()[0];
  const claims = getClaimsFromAccount(targetAccount);
  if (!claims) {
    return [];
  }

  return readStringArrayClaim(claims, 'roles');
}

export function hasMsalRole(roleName: string, account?: AccountInfo | null) {
  const normalized = roleName.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  return getMsalRoles(account).some(
    (role) => role.trim().toLowerCase() === normalized,
  );
}

export function isCurrentUserContractAdmin(account?: AccountInfo | null) {
  return hasMsalRole('ContractAdmin', account);
}

type GraphMeResponse = {
  jobTitle?: string | null;
  mail?: string | null;
  userPrincipalName?: string | null;
};

export async function getMsalJobTitleOrEmail(account?: AccountInfo | null) {
  const targetAccount =
    account ??
    msalInstance.getActiveAccount() ??
    msalInstance.getAllAccounts()[0];
  if (!targetAccount) {
    return '';
  }

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      account: targetAccount,
      scopes: ['User.Read'],
    });

    const response = await fetch(
      'https://graph.microsoft.com/v1.0/me?$select=jobTitle,mail,userPrincipalName',
      {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
      },
    );

    if (response.ok) {
      const data = (await response.json()) as GraphMeResponse;
      return (
        data.jobTitle?.trim() ||
        data.mail?.trim() ||
        data.userPrincipalName?.trim() ||
        getEmailFromAccount(targetAccount)
      );
    }
  } catch {
    // Fall through to claim-based values when Graph call isn't available.
  }

  return (
    getJobTitleFromAccount(targetAccount) || getEmailFromAccount(targetAccount)
  );
}
